import i18n from 'i18next';
import { formatTemplateValue } from "./templateTagFormatting";
import { getAllTagKeys } from "./templateTagResolvers";
import { EmailConnectedFeature, TemplateTag, templateTagsByFeature } from "./templateTags";

// Set the tag pattern which matches tags like {werkbon_nummer}
const TAG_PATTERN = /\{([a-z0-9_]+)\}/g;

// Set the tags which are resolved by the backend during sending
const BACKEND_RESOLVED_TAGS = ['proposal_portal_button'];

interface ResolveTemplateTagsProps {
    text: string;
    connectedFeature?: string | null;
    objectData?: any;
    currentUser?: any;
    locale?: string | null;
    language?: string | null;
    currency?: string | null;
    multilineSeparator?: string;
    keepUnknownTags?: boolean;
    keepEmptyTags?: boolean;
}

const getTemplateTagMap = (tags: TemplateTag[]) => {
    const tagMap = new Map<string, TemplateTag>();
    tags.forEach(tag => {
        getAllTagKeys(tag).forEach(key => {
            tagMap.set(key, tag);
        });
    });
    return tagMap;
};

// Find the tags in a text which are not converted to a value
export const findUnresolvedTags = (text: string): string[] => {
    const unresolvedTags: string[] = [];

    // Loop through the tag matches in the text
    let match;
    const pattern = new RegExp(TAG_PATTERN.source, 'g');
    while ((match = pattern.exec(text ?? '')) !== null) {
        const key = match[1];

        // Skip tags which the backend resolves during sending
        if (BACKEND_RESOLVED_TAGS.includes(key)) {
            continue;
        }

        // Add the tag once to the unresolved tags
        if (!unresolvedTags.includes(key)) {
            unresolvedTags.push(key);
        }
    }

    return unresolvedTags;
};

export const resolveTemplateTags = ({
    text,
    connectedFeature,
    objectData,
    currentUser,
    locale,
    language,
    currency,
    multilineSeparator,
    keepUnknownTags = true,
    keepEmptyTags = true
}: ResolveTemplateTagsProps): string => {
    let resolvedText = text ?? '';

    if (text && connectedFeature) {
        // Get the tags of the connected feature
        const tags = templateTagsByFeature[connectedFeature as EmailConnectedFeature] ?? [];

        // Generate a map of tags
        const tagMap = getTemplateTagMap(tags);

        // Create a translate function in the language of the environment
        const translate = (key: string, fallback: string): string => {
            return i18n.t(key, { defaultValue: fallback, lng: language ?? undefined }) as string;
        };

        // Loop through the text to find the tags which contain `{ }`
        resolvedText = text.replace(TAG_PATTERN, (match, key) => {

            // Find a matching tag from the tag map
            const tag = tagMap.get(key);

            // Replace the tag with the data of the object
            if (tag) {

                // Get the value from the object data or user preferences
                const value = tag.getValue(objectData, { currentUser, translate });

                // Format the value
                const formattedValue = formatTemplateValue(value, tag.type, {
                    locale: locale ?? undefined,
                    currency: currency ?? undefined,
                    multilineSeparator
                });

                // Return the formatted value if given
                if (formattedValue !== '') {
                    return formattedValue;
                }

                // When tag cannot be converted, do not replace the tag in the mail
                return keepEmptyTags ? match : '';
            }

            // When tag does not exist, do not replace the tag in the mail
            return keepUnknownTags ? match : '';
        });
    }

    return resolvedText;
};
